import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { getPostAuthStudioUrl } from '../routing/hostRouting';

/**
 * OAuth codes are single-use. React Strict Mode runs effects twice in dev, which
 * would call exchangeCodeForSession twice and the second POST .../token?grant_type=pkce
 * returns 400. Share one in-flight promise per code.
 */
const pkceExchangeByCode = new Map<
  string,
  ReturnType<typeof supabase.auth.exchangeCodeForSession>
>();

export const AuthCallback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const g = globalThis as unknown as { __AUDAFACT_AUTH_TRACE?: unknown[] };
        g.__AUDAFACT_AUTH_TRACE = g.__AUDAFACT_AUTH_TRACE ?? [];
        g.__AUDAFACT_AUTH_TRACE.push({
          step: 'callback_effect_start',
          t: Date.now(),
          href: typeof window !== 'undefined' ? window.location.href : null,
        });
        // #region agent log
        fetch(
          '/__agent-debug-log',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sessionId: '6faadc',
              hypothesisId: 'H2',
              location: 'AuthCallback.tsx:handleAuthCallback',
              message: 'callback entry',
              data: {
                host: typeof window !== 'undefined' ? window.location.hostname : null,
                origin: typeof window !== 'undefined' ? window.location.origin : null,
                pathname: typeof window !== 'undefined' ? window.location.pathname : null,
                hasOAuthCode: !!(searchParams.get('code')),
                hashPresent:
                  typeof window !== 'undefined' &&
                  !!window.location.hash?.length,
                authErrorParam: searchParams.get('error'),
                authErrorDescription: searchParams.get('error_description'),
              },
              timestamp: Date.now(),
            }),
          },
        ).catch(() => {});
        // #endregion
        // PKCE (OAuth) returns ?code=... on the callback URL, not a hash.
        const oauthCode = searchParams.get('code');
        if (oauthCode) {
          let inflight = pkceExchangeByCode.get(oauthCode);
          if (!inflight) {
            inflight = supabase.auth.exchangeCodeForSession(oauthCode);
            pkceExchangeByCode.set(oauthCode, inflight);
          }
          const { data: exchanged, error: exchangeError } = await inflight;
          // #region agent log
          fetch(
            '/__agent-debug-log',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId: '6faadc',
                hypothesisId: 'H9',
                location: 'AuthCallback.tsx:exchangeCodeForSession',
                message: 'after exchangeCodeForSession',
                data: {
                  host:
                    typeof window !== 'undefined'
                      ? window.location.hostname
                      : null,
                  exchangeError: exchangeError?.message ?? null,
                  hasSessionUser: !!exchanged?.session?.user,
                },
                timestamp: Date.now(),
              }),
            },
          ).catch(() => {});
          // #endregion
          g.__AUDAFACT_AUTH_TRACE?.push({
            step: 'after_exchange',
            t: Date.now(),
            exchangeError: exchangeError?.message ?? null,
            hasUser: !!exchanged?.session?.user,
          });
          if (!exchangeError && exchanged.session?.user) {
            const next = getPostAuthStudioUrl();
            // #region agent log
            fetch(
              '/__agent-debug-log',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  sessionId: '6faadc',
                  hypothesisId: 'H3',
                  location: 'AuthCallback.tsx:oauth exchange success',
                  message: 'replace after exchangeCodeForSession',
                  data: {
                    next,
                    host:
                      typeof window !== 'undefined'
                        ? window.location.hostname
                        : null,
                  },
                  timestamp: Date.now(),
                }),
              },
            ).catch(() => {});
            // #endregion
            window.location.replace(next);
            return;
          }
          // #region agent log
          fetch(
            '/__agent-debug-log',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId: '6faadc',
                hypothesisId: 'H10',
                location: 'AuthCallback.tsx:oauth branch fallthrough',
                message:
                  'had OAuth code but no session after exchange — fallthrough',
                data: {
                  host:
                    typeof window !== 'undefined'
                      ? window.location.hostname
                      : null,
                  exchangeError: exchangeError?.message ?? null,
                },
                timestamp: Date.now(),
              }),
            },
          ).catch(() => {});
          // #endregion
          g.__AUDAFACT_AUTH_TRACE?.push({
            step: 'oauth_fallthrough',
            t: Date.now(),
          });
          // If the client already auto-exchanged, or the code was consumed, fall through.
        }

        // Check if we have authentication data in the hash
        if (window.location.hash && window.location.hash.includes('access_token')) {
          // Parse the hash to extract the access token
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          const type = hashParams.get('type');
          
          if (accessToken) {
            // Set the session manually with the tokens from the hash
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || '',
            });
            
            if (error) {
              setError(error.message);
              setLoading(false);
              return;
            }

            if (data.session?.user) {
              // Check if this is a fresh email verification
              const user = data.session.user;
              const emailVerifiedAt = user.email_confirmed_at;
              const now = new Date();
              const verifiedAt = emailVerifiedAt ? new Date(emailVerifiedAt) : null;
              
              // If this is a signup type OR if email was verified very recently (within 2 minutes)
              if (type === 'signup' || (verifiedAt && (now.getTime() - verifiedAt.getTime()) < 2 * 60 * 1000)) {
                window.location.replace(
                  getPostAuthStudioUrl('?verified=true&type=signup'),
                );
                return;
              }
              
              // Otherwise, go directly to studio
              window.location.replace(getPostAuthStudioUrl());
              return;
            }
          }
        }
        
        // If no hash with access token, wait a moment and check for session
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Check if we have a session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          setError(error.message);
          setLoading(false);
          return;
        }

        if (session?.user) {
          window.location.replace(getPostAuthStudioUrl());
        } else {
          // Check for error parameters in URL
          const errorParam = searchParams.get('error');
          const errorDescription = searchParams.get('error_description');
          
          if (errorParam) {
            setError(`Authentication failed: ${errorDescription || errorParam}`);
            setLoading(false);
            return;
          }
          
          navigate('/auth', { replace: true });
        }
      } catch (err) {
        setError('An unexpected error occurred');
        setLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-audafact-surface-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-audafact-accent-cyan mx-auto mb-4"></div>
          <p className="audafact-text-secondary">Completing sign in...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-audafact-surface-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-audafact-alert-red mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold audafact-heading mb-2">Sign In Failed</h2>
          <p className="audafact-text-secondary mb-4">{error}</p>
          <button
            onClick={() => navigate('/auth')}
            className="audafact-button-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return null;
}; 